import { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { DateRangePickerProps } from '@cloudscape-design/components';

import { getApiClient } from '../../api/client';
import type { components } from '../../api/schema';
import { buildListQueryParams, withHiddenFilter } from './query';

type CollectRequest = components['schemas']['CollectRequest'];
type SearchRequest = components['schemas']['SearchRequest'];

export async function getPlaces(
  query: PropertyFilterQuery,
  page = 1,
  limit = 20,
  range?: DateRangePickerProps.Value | null,
  sortingField?: string,
  sortDirection?: 'asc' | 'desc',
  now = new Date(),
  textQuery?: string,
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query: withHiddenFilter(query),
    page, limit, range, now,
    rangeName: 'updated_at',
    sortingField: sortingField ?? 'updated_at',
    sortDirection: sortDirection ?? 'desc',
    textQuery,
  });
  const { data, error } = await client.GET('/places', { params: { query: params } });
  if (error) throw new Error(`Failed to fetch places: ${JSON.stringify(error)}`);
  return data;
}

export async function getPlaceMeta() {
  const client = getApiClient();
  const { data, error } = await client.GET('/places/meta');
  if (error) throw new Error(`Failed to fetch places meta: ${JSON.stringify(error)}`);
  return data;
}

export async function getPlace(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/places/{uuid}', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to fetch place: ${JSON.stringify(error)}`);
  return data;
}

export async function collectPlaces(body: CollectRequest) {
  const client = getApiClient();
  const { data, error } = await client.POST('/places/collect', { body });
  if (error) throw new Error(`Failed to collect places: ${JSON.stringify(error)}`);
  return data;
}

export async function searchPlaces(body: SearchRequest) {
  const client = getApiClient();
  const { data, error } = await client.POST('/places/search', { body });
  if (error) throw new Error(`Failed to search places: ${JSON.stringify(error)}`);
  return data;
}

export async function hidePlace(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/places/{uuid}/hide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to hide place: ${JSON.stringify(error)}`);
}

export async function unhidePlace(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/places/{uuid}/unhide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to unhide place: ${JSON.stringify(error)}`);
}

export async function hidePlaces(uuids: string[]) {
  for (const uuid of uuids) await hidePlace(uuid);
}

export async function unhidePlaces(uuids: string[]) {
  for (const uuid of uuids) await unhidePlace(uuid);
}

export async function getPlaceWebsiteContacts(
  uuid: string,
  query: PropertyFilterQuery,
  page = 1,
  limit = 20,
  range?: DateRangePickerProps.Value | null,
  sortingField?: string,
  sortDirection?: 'asc' | 'desc',
  now = new Date(),
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query: withHiddenFilter(query),
    page, limit, range, now,
    rangeName: 'updated_at',
    sortingField: sortingField ?? 'updated_at',
    sortDirection: sortDirection ?? 'desc',
  });
  const { data, error } = await client.GET('/places/{uuid}/website-contacts', {
    params: { path: { uuid }, query: params },
  });
  if (error) throw new Error(`Failed to fetch website contacts: ${JSON.stringify(error)}`);
  return data;
}

export async function getPlaceWebsiteContactMeta(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/places/{uuid}/website-contacts/meta', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to fetch website contacts meta: ${JSON.stringify(error)}`);
  return data;
}

export async function getPlaceZoomInfoContacts(
  uuid: string,
  page = 1,
  limit = 20,
  sortingField?: string,
  sortDirection?: 'asc' | 'desc',
  now = new Date(),
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query: { tokens: [], operation: 'and' },
    page,
    limit,
    now,
    rangeName: 'last_enriched_at',
    sortingField: sortingField ?? 'contact_accuracy_score',
    sortDirection: sortDirection ?? 'desc',
  });
  const { data, error } = await client.GET('/places/{uuid}/zoominfo-contacts', {
    params: { path: { uuid }, query: params },
  });
  if (error) throw new Error(`Failed to fetch ZoomInfo contacts: ${JSON.stringify(error)}`);
  return data;
}

export async function getPlaceZoomInfoCompany(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/places/{uuid}/zoominfo-company', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to fetch ZoomInfo company: ${JSON.stringify(error)}`);
  return data;
}
